import { createFrontDeviceTelemetrySaga } from '../../Front/Device/Telemetry/deviceTelemetrySagas';
import { DeviceTelemetryType } from '@signageos/common-types/dist/Device/Telemetry/DeviceTelemetryType';
import { MonitoringLogData } from '@signageos/common-types/dist/Device/MonitoringLog/MonitoringLogData';
import { IPropertyStorage } from '../../Property/propertyStorage';
import { getSupraCoreAppDefaultConfig, IServerSupraScreenConfig } from './supraConfig';
import { ISupraRuntimeState } from './supraRuntimeState';
import Property from '../../Property/Property';
import { call, fork } from 'redux-saga/effects';
import { awaitDependencies, withDependencies } from '../../DI/dependencyInjection';
import FrontCapability from '../../NativeDevice/Front/FrontCapability';
import { SagaIterator } from 'redux-saga';
import Debug from 'debug';

const debug = Debug('@signageos/front-display:Front:Supra:supraConnectionTelemetrySagas');

export function* telemetrySupraConnectionSaga(): SagaIterator {
	let currentSupraRuntimeState: ISupraRuntimeState | null = null;
	const { propertyStorage } = yield call(awaitDependencies, ['propertyStorage']);
	yield fork(
		withDependencies(['supraRuntimeState'], function* ({ supraRuntimeState }) {
			debug('supraRuntimeState loaded', supraRuntimeState);
			currentSupraRuntimeState = supraRuntimeState ?? null;
		}),
	);

	const telemetrySaga = createFrontDeviceTelemetrySaga(
		DeviceTelemetryType.SUPRA,
		[FrontCapability.SUPRA],
		getSupraConnectionTelemetry(propertyStorage, () => currentSupraRuntimeState ?? null),
	);
	yield call(telemetrySaga);
}

/**
 * Creates a telemetry getter that reads Supra connection state from the provided getter
 * instead of module-level global state.
 */
export function getSupraConnectionTelemetry(
	propertyStorage: IPropertyStorage,
	getSupraRuntimeState: () => ISupraRuntimeState | null,
): () => Promise<MonitoringLogData[DeviceTelemetryType.SUPRA]> {
	debug('getSupraConnectionTelemetry created', { supraRuntimeState: getSupraRuntimeState() });
	return async (): Promise<MonitoringLogData[DeviceTelemetryType.SUPRA]> => {
		const config = await propertyStorage.getValueOrDefault<IServerSupraScreenConfig | null>(Property.SUPRA_SCREEN_CONFIG, null);
		debug('supraRuntimeState getSupraConnectionTelemetry', { config, supraRuntimeState: getSupraRuntimeState() });
		if (!config || !config.servers || config.servers.length === 0) {
			return { enabled: false };
		}

		const runtimeState = getSupraRuntimeState();
		const defaults = getSupraCoreAppDefaultConfig();

		// Derive connectionStatus from timing controller runtime state:
		// - null (never evaluated) → 'searching'
		// - isActive=true → 'connected' (timing set to supra-screen)
		// - isActive=false → 'disconnected' (servers exist but applet is not supra-screen)
		let connectionStatus: MonitoringLogData[DeviceTelemetryType.SUPRA]['connectionStatus'];
		if (runtimeState === null) {
			connectionStatus = 'searching';
		} else if (runtimeState.isActive) {
			connectionStatus = 'connected';
		} else {
			connectionStatus = 'disconnected';
		}

		// When connected, use runtime state from the timing controller (the resolved available server)
		// When not connected, fall back to default config values
		const connection = runtimeState?.connection;

		return {
			enabled: true,
			hostname: connection?.hostname,
			secure: connection?.secure,
			appName: connection?.appName ?? defaults.appName,
			extraArgs: connection?.extraArgs,
			supraCoreAppVersion: connection?.supraCoreAppVersion,
			supraScreenAppletVersion: connection?.supraScreenAppletVersion ?? defaults.supraScreenAppletVersion,
			clientID: connection?.clientID,
			servers: config.servers.map((s) => ({
				hostname: s.hostname,
				secure: s.secure,
			})),
			connectionStatus,
		};
	};
}
